// Home v2 — portfolio allocation donut. One slice per token, sized by the
// token's USD value (public portfolio tokens, or railgun tokens in private
// mode). The long tail past the palette size is grouped into "Others".
import React, { useMemo } from 'react'
import { View } from 'react-native'
import Svg, { Circle } from 'react-native-svg'

import { getTokenBalanceInUSD } from '@ambire-common/libs/portfolio/helpers'
import formatDecimals from '@ambire-common/utils/formatDecimals/formatDecimals'
import RollingText from '@common/components/RollingText'
import Text from '@common/components/Text'
import useTheme from '@common/hooks/useTheme'

import { AssetRowData } from '../useHomeDashboard'
import { CHART_OTHERS, CHART_PALETTE, RADII } from '../tokens'

type Props = {
  assets: AssetRowData[]
  // Controller portfolio total (the header's "Estimated Balance"). The donut
  // mirrors this so the two numbers never disagree; value the header counts but
  // the visible token list drops (gas-tank, hidden, borrow, dust) folds into
  // "Others". Omit to fall back to the sum of visible tokens.
  totalUsd?: number
  hidden?: boolean
  // While the portfolio is still fetching, the donut would otherwise mirror an
  // empty (0) total and flash "Total $0.00 / No assets to allocate" — looking
  // broken next to the skeleton token list. When loading and we have nothing
  // valued yet, show a neutral loading state instead of the empty state.
  loading?: boolean
}

const SIZE = 200
const R = 80
const STROKE = 28
const C = 2 * Math.PI * R
// Visual gap between slices, in circumference units (butt caps, no overhang).
const GAP = 6
const MAX_SLICES = CHART_PALETTE.length

type Slice = {
  key: string
  label: string
  value: number
  color: string
}

const PortfolioAllocation: React.FC<Props> = ({
  assets,
  totalUsd,
  hidden = false,
  loading = false
}) => {
  const { theme } = useTheme()

  const { slices, total } = useMemo(() => {
    const valued = assets
      .map((a) => ({ symbol: a.symbol, id: a.id, value: getTokenBalanceInUSD(a.raw) }))
      .filter((t) => t.value > 0)
      .sort((a, b) => b.value - a.value)

    const visibleSum = valued.reduce((acc, t) => acc + t.value, 0)
    // Donut total mirrors the header balance; anything the header counts but the
    // visible tokens don't (gas-tank, hidden, borrow, dust) becomes residual and
    // folds into "Others" so the slices still sum to 100%.
    const chartTotal = totalUsd != null && totalUsd > 0 ? totalUsd : visibleSum
    const residual = Math.max(0, chartTotal - visibleSum)

    const head = valued.slice(0, MAX_SLICES)
    const tail = valued.slice(MAX_SLICES)

    const result: Slice[] = head.map((t, i) => ({
      key: t.id,
      label: t.symbol,
      value: t.value,
      color: CHART_PALETTE[i]
    }))
    const othersValue = tail.reduce((acc, t) => acc + t.value, 0) + residual
    if (othersValue > 0) {
      result.push({
        key: 'others',
        label: 'Others',
        value: othersValue,
        color: CHART_OTHERS
      })
    }
    return { slices: result, total: chartTotal }
  }, [assets, totalUsd])

  const isEmpty = total <= 0
  // Still fetching and nothing priced yet → neutral loading state, not "$0.00".
  const isLoading = loading && isEmpty
  // eslint-disable-next-line no-nested-ternary
  const totalLabel = hidden ? '••••••••' : isLoading ? '—' : formatDecimals(total, 'value')

  // Precompute each arc's visible length and starting offset on the ring.
  const arcs = useMemo(() => {
    if (isEmpty) return []
    let cursor = 0
    return slices.map((s) => {
      const frac = s.value / total
      const arcLen = frac * C
      const start = cursor
      cursor += arcLen
      // Shrink for the gap; keep a sliver visible for tiny slices.
      const visible = Math.max(arcLen - GAP, 1)
      return { ...s, visible, start }
    })
  }, [slices, total, isEmpty])

  return (
    <View
      style={{
        backgroundColor: theme.homeCardBackground as string,
        borderRadius: RADII.lg,
        padding: 16,
        gap: 16
      }}
    >
      <View style={{ gap: 2 }}>
        <Text fontSize={18} weight="semiBold">
          Portfolio allocation
        </Text>
        <Text fontSize={12} appearance="secondaryText">
          By USD value
        </Text>
      </View>

      <View style={{ width: SIZE, height: SIZE, alignSelf: 'center' }}>
        <Svg width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
          {isEmpty ? (
            <Circle
              cx={100}
              cy={100}
              r={R}
              fill="none"
              stroke={theme.homeCardBorder as string}
              strokeWidth={STROKE}
            />
          ) : (
            arcs.map((a) => (
              <Circle
                key={a.key}
                cx={100}
                cy={100}
                r={R}
                fill="none"
                stroke={a.color}
                strokeWidth={STROKE}
                strokeDasharray={`${a.visible} ${C - a.visible}`}
                strokeDashoffset={-a.start}
                transform="rotate(-90 100 100)"
              />
            ))
          )}
        </Svg>
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Text fontSize={14} weight="medium" appearance="secondaryText">
            Total
          </Text>
          <RollingText fontSize={28} weight="number_bold" value={totalLabel} />
        </View>
      </View>

      <View style={{ flexDirection: 'row', flexWrap: 'wrap', justifyContent: 'center', gap: 12 }}>
        {isEmpty ? (
          <Text fontSize={12} appearance="secondaryText">
            {isLoading ? 'Loading allocation…' : 'No assets to allocate'}
          </Text>
        ) : (
          slices.map((s) => (
            <View key={s.key} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
              <View style={{ width: 8, height: 8, borderRadius: 2, backgroundColor: s.color }} />
              <Text fontSize={12} weight="semiBold">
                {s.label}
              </Text>
              <Text fontSize={12} appearance="secondaryText">
                ({((s.value / total) * 100).toFixed(1)}%)
              </Text>
            </View>
          ))
        )}
      </View>
    </View>
  )
}

export default React.memo(PortfolioAllocation)
